import {descriptionTemplateLabel} from "../utils/workflow-item-description";
import {invoiceNumberTemplateLabel} from "../utils/workflow-invoice-number";
import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash} from "@fortawesome/free-solid-svg-icons";
import {fetch} from "../utils/api-wrapper";
import {loadXpensCategories, getXpensCategoryLabel} from "../utils/xpens-categories";
import {imputationRowHasParentId} from "../utils/expenses-imputations-config-helpers";
import {conditionTypeLabels, TYPES_WITH_OPERATOR} from "./workflow-condition-labels";
import {imputationDocumentId, mongoExtendedJsonId} from "../pages/review-expense/utils/contract-expense-json";
import {isNegatingUserOperator} from "../utils/workflow-condition-operators";
import {cachedFetch} from "../utils/cached-api-wrapper";
import Utils from "../utils/utils";
import {glAccountClassNode} from "../utils/gl-account-class";

// XPEN-3206 and XPEN-3414: the two conditions that read a field off a line's GL
// account, and the option list each resolves its codes against.
const GL_ACCOUNT_CONDITION_LOOKUPS = {
    item_gl_class: "GLAcctClass",
    item_gl_category: "GLAcctCategoryList",
};

const isGLSectionWorkflowVariable = (value) =>
    value?.type === "workflow_variable" &&
    value?.value === "first_4_digits_of_gl_natural";

const REQUEST_INVOICE_APPROVAL = "request_invoice_approval";
const REQUEST_INVOICE_ANY_OF_APPROVAL = "request_invoice_any_of_approval";
const REQUEST_LINE_ITEM_APPROVAL = "request_line_item_approval";

const WorkflowStepDisplayCard = ({
                                     step,
                                     onClickFunc,
                                     isSelected,
                                     data,
                                     expenseRates,
                                     clientImputations = [],
                                     branch,
                                     removeStep,
                                     validateCondition, validateStep, validateAction,
                                     index,
                                 }) => {
    // Local hover state to conditionally show the remove button.
    const [isHover, setIsHover] = useState(false);
    const [approvers, setApprovers] = useState([]);
    const [departments, setDepartments] = useState([]);
    // code -> description. The rule stores only the code, so the description has
    // to be looked up to render the class the way the picker offers it.
    const [glAccountClasses, setGlAccountClasses] = useState({});
    const [glAccountCategories, setGlAccountCategories] = useState({});
    const [sections, setSections] = useState({});

    // ------------------ HELPER FUNCTIONS ------------------

    // Fetch approvers on component mount.
    useEffect(() => {
        fetchApprovers();
        fetchDepartments();
        fetchCodeOptions();
    }, []);

    // XPEN-3187: safe no-op if already cached by App.js's session-level fetch.
    useEffect(() => {
        loadXpensCategories(data);
    }, [data]);

    const fetchApprovers = () => {
        fetch("/client/users/approvers", "GET", {}, (response) => {
            setApprovers(response.data);
        });
    };

    const fetchDepartments = () => {
        const filters = [{id: "id_client", value: data.id_client}];
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify([]))}&start=${encodeURIComponent(0)}&page_size=${100}`;
        fetch(`/departments/${searchData}`, "GET", {}, (response) => {
            setDepartments(response.data.departments || []);
        });
    };

    // Shares the workflow editor's cache entries, so opening a rule and its editor
    // costs one request each between them. Elite-only, like what reads them.
    const fetchCodeOptions = () => {
        if (!Utils.isExportFormat(data, "elite")) return;
        [["GLAcctClass", setGlAccountClasses],
         ["GLAcctCategoryList", setGlAccountCategories],
         ["Section", setSections]].forEach(([fieldName, store]) => {
            cachedFetch(`/client/options/?field_name=${fieldName}&filters=${encodeURIComponent("[]")}`,
                "GET", {},
                (response) => {
                    const rows = Array.isArray(response?.data) ? response.data : [];
                    store(Object.fromEntries(
                        rows.filter(row => row?.Id).map(row => [row.Id, row.Description])));
                });
        });
    };

    const approverMap = (approver) => {
        return {
            value: approver?.id_user,
            label: `${approver?.first_name} ${approver?.last_name}`,
        };
    };

    // If no step is provided, do not render anything.
    if (!step) return null;

    // ------------------ HELPER FUNCTIONS ------------------
    const categoryLabel = (category) => getXpensCategoryLabel(data, category);

    const clientCategoryLabel = (value) => {
        const needle = (mongoExtendedJsonId(value) || "").toLowerCase();
        if (!needle) return data.t.read("n_a");
        const row = (clientImputations || []).find(
            (imputation) =>
                !imputationRowHasParentId(imputation)
                && imputationDocumentId(imputation).toLowerCase() === needle
        );
        return row?.category || mongoExtendedJsonId(value) || data.t.read("n_a");
    };

    const userLabel = (user) => {
        if (!user) return "";
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        return fullName || user.username || user.email || data.t.read("n_a");
    };

    const glBetweenLabel = (value) => {
        if (typeof value !== "object" || value === null) return "";
        return [value.min, value.max].filter(Boolean).join(` ${data.t.read("rule.and")} `);
    };
    /**
     * getConditionLabel
     * Returns a summary label for a condition.
     */
    const getConditionLabel = (condition) => {
        // Shared with the branch precondition badge so the two cannot drift.
        const conditionMap = conditionTypeLabels(data.t);

        // Start with the translated label or the raw type.
        let label = conditionMap[condition.type] || condition.type;

        // For some condition types, include the operator.
        if (TYPES_WITH_OPERATOR.includes(condition.type)) {
            label += ` ${data.t.read(`rule.${condition.operator}`)}`;
        }

        // XPEN-3206: the editor offers this one "is" / "is not", so read it back the
        // same way. The shared rule.<operator> keys above would print "equals", and
        // in French "est égale à" -- and dropping the operator entirely would make a
        // rule and its opposite render identically.
        if (condition.type === "item_gl_class" || condition.type === "item_gl_category") {
            label += ` ${data.t.read(condition.operator === "different"
                ? "rule.operator_is_not"
                : "rule.operator_is")}`;
        }

        // Append value or role details for various condition types.
        if (condition.type === "invoice_billable") {
            label = condition.value === "1"
                ? ` ${data.t.read("rule.the_invoice_is_billable")}`
                : ` ${data.t.read("rule.the_invoice_is_not_billable")}`;
        } else if (condition.type === "item_billable") {
            label = condition.value === "1"
                ? data.t.read("rule.the_line_item_is_billable")
                : data.t.read("rule.the_line_item_is_not_billable");
        } else if (condition.type === "user_has_role" && condition.role) {
            label += `: ${data.t.read("role_" + condition.role.toLowerCase())}`;
        } else if (condition.type === "user_has_function" && condition.value) {
            label += `: ${data.t.read(condition.value)}`;
        } else if (condition.type === "invoice_custom") {
            label += condition.value === "1"
                ? `: ${data.t.read("rule.invoice_custom")}`
                : `: ${data.t.read("rule.invoice_not_custom")}`;
        }
        else if (condition.type === "item_gl" && condition.operator === "between") {
            const range = glBetweenLabel(condition.value);
            label += range ? `: ${range}` : "";
        }
        else if (GL_ACCOUNT_CONDITION_LOOKUPS[condition.type] && condition.value) {
            // Returned rather than appended: these carry the code picked out, so
            // they are nodes, not strings. Each code falls back to itself while the
            // lookup is in flight, and for one 3E no longer returns. The rule stores
            // "one or more" as a comma-joined string, which is what the engine splits.
            const lookup = GL_ACCOUNT_CONDITION_LOOKUPS[condition.type] === "GLAcctClass"
                ? glAccountClasses : glAccountCategories;
            const codes = String(condition.value).split(",").map(code => code.trim()).filter(Boolean);
            return <>{label}: {codes.map((code, index) => (
                <React.Fragment key={code}>
                    {index > 0 ? ", " : ""}{glAccountClassNode(code, lookup[code])}
                </React.Fragment>
            ))}</>;
        }
        else if (condition.type === "line_item_amount" || condition.type === "amount_lower" || condition.type === "amount_greater"){
            label += `: $${condition.value}`;
        }
        else if (condition.type === "purchase_and_payment") {
            label += `: ${data.t.read("rule." + condition.value)}`;
        }
        else if (condition.type === "invoice_type") {
            label += `: ${data.t.read("rule.invoice_type_" + condition.value)}`;
        }
        else if (condition.type === "category") {
            label += `: ${categoryLabel(condition.value)}`;
        }
        else if (condition.type === "client_category") {
            label += `: ${clientCategoryLabel(condition.value)}`;
        }
        else if (condition.type === "user_is") {
            const users = Array.isArray(condition.value) ? condition.value : [condition.value].filter(Boolean);
            // The engine decides "this rule names nobody" on the presence of
            // id_user, not on whether a display name can be built, so key off the
            // same field (RulesProcessor, XPEN-3347). An entry with no id_user is
            // one the engine ignores entirely; showing it would overstate the rule.
            const namedUsers = users.filter(user => user && user.id_user);
            const names = namedUsers.map(userLabel).filter(Boolean).join(", ");

            // XPEN-3348: read the operator back with the user-specific wording. The
            // shared rule.<operator> keys used above would print "is different", and
            // in French "est égale à" -- wrong for a person.
            //
            // Only when the rule names someone. A condition naming nobody matches
            // nothing under either operator, so a bare "the user is not" would
            // assert the opposite of what the engine does.
            if (namedUsers.length) {
                label += ` ${data.t.read(isNegatingUserOperator(condition.operator)
                    ? "rule.user_operator_is_not"
                    : "rule.user_operator_is")}: ${names}`;
            }
        }
        else if (condition.type === "user_in_section") {
            return <>{label}: {glAccountClassNode(condition.value, sections[condition.value])}</>;
        }
        else if (condition.type === "user_in_department") {
            const departments = Array.isArray(condition.value) ? condition.value : [condition.value].filter(Boolean);
            const names = departments
                .map((dep) => dep?.name || dep?.label || "")
                .filter(Boolean)
                .join(", ");
            label += names ? `: ${names}` : "";
        }
        else if (
            ![
                "invoice_billable",
                "item_billable",
                "user_has_role",
                "invoice_custom",
                "line_item_amount",
            ].includes(condition.type) &&
            condition.value
        ) {
            label += `: ${condition.value}`;
        }
        return label;
    };

    /**
     * getActionLabel
     * Returns a summary label for an action.
     */
    const getActionLabel = (action) => {
        // Mapping of action types to translated labels.
        const actionMap = {
            send_email: data.t.read("rule.send_email_to"),
            split_items: data.t.read("rule.split_items"),
            split_mileage_by_secondary_rate: data.t.read("rule.split_mileage_by_secondary_rate"),
            split_tip_into_separate_item: data.t.read("rule.split_tip_into_separate_item"),
            only_keep_total: data.t.read("rule.only_keep_total"),
            change_currency: data.t.read("rule.change_currency_for"),
            change_all_totals: data.t.read("rule.change_all_totals"),
            change_user: data.t.read("rule.change_user"),
            change_item_description: data.t.read("rule.change_item_description"),
            change_item_detail: data.t.read("rule.change_item_detail"),
            request_section_approval: data.t.read("rule.request_section_approval"),
            change_status: data.t.read("rule.change_status"),
            change_invoice_default_field_value: data.t.read("rule.change_invoice_default_field_value"),
            apply_gl_description_to_items: data.t.read("rule.apply_gl_description_to_items"),
            achat_and_payment: `${data.t.read("activate")} ${data.t
                .read("achat_and_payment")
                .toLowerCase()}`,
            set_invoice_number: data.t.read("rule.set_invoice_number"),
            use_invoice_date_as_invoice_number: data.t.read("rule.use_invoice_date_as_invoice_number"),
            request_invoice_approval: data.t.read("rule.request_invoice_approval"),
            request_invoice_any_of_approval: data.t.read("rule.request_invoice_any_of_approval"),
            request_approval: data.t.read("rule.request_approval_from_user"),
            request_invoice_user_approval: data.t.read("rule.request_approval_from_invoice_user"),
            request_line_item_approval: data.t.read("rule.request_line_item_approval"),
            request_line_item_department_approval: data.t.read("rule.request_line_item_department_approval"),
            request_line_item_matter_responsible_approval: data.t.read("rule.request_line_item_approval_from_matter_responsible"),
            request_line_item_gl_responsible_approval: data.t.read("rule.request_line_item_approval_from_gl_responsible"),
            request_line_item_category_responsible_approval: data.t.read("rule.request_line_item_approval_from_category_responsible"),
            request_line_item_subcategory_responsible_approval: data.t.read("rule.request_line_item_approval_from_subcategory_responsible"),
            request_department_approval: data.t.read("rule.request_department_approval"),
            request_matter_responsible_approval: data.t.read("rule.request_approval_from_matter_responsible"),
            request_gl_responsible_approval: data.t.read("rule.request_approval_from_gl_responsible"),
            tax_deduction: data.t.read("rule.apply_tax_deduction"),
            change_unit: data.t.read("rule.change_the_unit_to"),
            delay_approval: data.t.read("rule.delay_approval"),
            "append_approver_list": data.t.read("rule.add_to_approver_list"),
            exclude_approver_roles: data.t.read("rule.exclude_approver_roles"),
            change_tax_codes: data.t.read("rule.change_the_tax_code_to"),
            change_cheque_reference: data.t.read("rule.change_cheque_reference"),
            change_due_date: data.t.read("rule.change_due_date"),
            rename_invoice_file: data.t.read("rule.rename_invoice_file"),
            change_item_tax_code: data.t.read("rule.change_item_tax_code"),
            change_reference_to_matter_number: data.t.read("rule.change_reference_to_matter_number"),
            delete_line_item: data.t.read("rule.delete_line_item"),
            sanitize_invoice_number_spaces: data.t.read("rule.sanitize_invoice_number_spaces"),
        };

        let label = actionMap[action.type] || action.type;

        // For many action types, append the action's value.
        if (
            [
                "change_user",
            "change_currency",
            "send_email",
            "change_status",
            "change_unit",
            "change_tax_codes",
            "change_due_date",
            "change_item_tax_code",
            "change_cheque_reference",
        ].includes(action.type) &&
        action.value
    ) {
        if (action.type === "change_due_date") {
            if (action.value === "on_day_next_month") {
                label += `: ${data.t.read("rule.on_day_next_month")} ${action.day}`;
            } else {
                label += `: ${data.t.read(`rule.${action.value}`)}`;
            }
        } else {
            label += `: ${action.value}`;
        }
        } else if (action.type === "set_invoice_number") {
            label += `: ${invoiceNumberTemplateLabel(action.value, key => data.t.read(key))}`;
        } else if (action.type === "request_section_approval") {
            // Read back in the order the ticket words the rule: approval of X,
            // from someone with role Y, of section Z. The section is a node so
            // its code reads the way the picker offered it.
            const value = action.value || {};
            const target = data.t.read(value.target === "line_item"
                ? "rule.section_approval_target_line_item"
                : "rule.section_approval_target_invoice");
            const role = value.role ? data.t.read("role_" + String(value.role).toLowerCase()) : null;
            return <>
                {label}: {target}
                {role ? `, ${role}` : ""}
                {value.section ? <>, {glAccountClassNode(value.section, sections[value.section])}</> : ""}
            </>;
        } else if (action.type === REQUEST_INVOICE_APPROVAL) {
            const source = action.value?.approver_source;
            const sourceMap = {
                manual_user: data.t.read("rule.request_approval_from_user"),
                invoice_user: data.t.read("rule.request_approval_from_invoice_user"),
                gl_responsible: data.t.read("rule.request_approval_from_gl_responsible"),
                matter_responsible: data.t.read("rule.request_approval_from_matter_responsible"),
                matter_practice_group_timekeeper: data.t.read("rule.request_approval_from_matter_practice_group_timekeeper"),
                department: data.t.read("rule.request_department_approval"),
            };
            const parts = [sourceMap[source]].filter(Boolean);

            if (source === "manual_user" && action.value?.user_id) {
                const approver = approvers.find((a) => a.id_user === action.value.user_id);
                if (approver) parts.push(approverMap(approver).label);
            } else if (source === "department") {
                const department = departments.find((d) => (d._id?.$oid || d._id) === action?.value?.department);
                const details = [department?.name, action?.value?.function].filter(Boolean).join(" / ");
                if (details) parts.push(details);
            }

            if (parts.length > 0) {
                label = parts.join(" - ");
            }
        } else if (action.type === REQUEST_INVOICE_ANY_OF_APPROVAL && Array.isArray(action.value)) {
            const specLabels = action.value.map((spec) => {
                const src = spec?.approver_source;
                if (src === "manual_user") {
                    if (spec?.user_id) {
                        const approver = approvers.find((a) => a.id_user === spec.user_id);
                        if (approver) return approverMap(approver).label;
                    }
                    return data.t.read("rule.request_approval_from_user");
                }
                if (src === "department") {
                    const department = departments.find((d) => (d._id?.$oid || d._id) === spec?.department);
                    const details = [department?.name, spec?.function].filter(Boolean).join(" / ");
                    return details || data.t.read("rule.request_department_approval");
                }
                const sourceMap = {
                    invoice_user: data.t.read("rule.request_approval_from_invoice_user"),
                    gl_responsible: data.t.read("rule.request_approval_from_gl_responsible"),
                    matter_responsible: data.t.read("rule.request_approval_from_matter_responsible"),
                    matter_practice_group_timekeeper: data.t.read("rule.request_approval_from_matter_practice_group_timekeeper"),
                };
                return sourceMap[src] || "";
            }).filter(Boolean);
            if (specLabels.length > 0) {
                label = `${data.t.read("rule.request_invoice_any_of_approval")}: ${specLabels.join(", ")}`;
            }
        } else if (action.type === REQUEST_LINE_ITEM_APPROVAL && typeof action.value === "object" && action.value !== null) {
            const source = action.value?.approver_source;
            const sourceMap = {
                manual_user: data.t.read("rule.request_line_item_approval"),
                gl_responsible: data.t.read("rule.request_line_item_approval_from_gl_responsible"),
                matter_responsible: data.t.read("rule.request_line_item_approval_from_matter_responsible"),
                matter_practice_group_timekeeper: data.t.read("rule.request_line_item_approval_from_matter_practice_group_timekeeper"),
                department: data.t.read("rule.request_line_item_department_approval"),
                category_responsible: data.t.read("rule.request_line_item_approval_from_category_responsible"),
                subcategory_responsible: data.t.read("rule.request_line_item_approval_from_subcategory_responsible"),
                subcategory_approvers: data.t.read("rule.request_line_item_approval_from_subcategory_approvers"),
            };
            const parts = [sourceMap[source]].filter(Boolean);

            if (source === "subcategory_approvers" && action.value?.role) {
                parts.push(data.t.read("role_" + String(action.value.role).toLowerCase()) || action.value.role);
            }

            if (source === "manual_user" && action.value?.user_id) {
                const approver = approvers.find((a) => a.id_user === action.value.user_id);
                if (approver) parts.push(approverMap(approver).label);
            } else if (source === "department") {
                const department = departments.find((d) => (d._id?.$oid || d._id) === action?.value?.department);
                const details = [department?.name, action?.value?.function].filter(Boolean).join(" / ");
                if (details) parts.push(details);
            }

            if (parts.length > 0) {
                label = parts.join(" - ");
            }
        } else if (["request_approval", "request_line_item_approval"].includes(action.type) && action.value) {
            const approver = approvers.find((a) => a.id_user === action.value);
            if (approver) {
                label += ` ${approverMap(approver).label}`;
            }
        } else if (["request_department_approval", "request_line_item_department_approval"].includes(action.type)) {
            const department = departments.find((d) => (d._id?.$oid || d._id) === action?.value?.department);
            const functionName = action?.value?.function;
            const details = [department?.name, functionName].filter(Boolean).join(" / ");
            if (details) {
                label += `: ${details}`;
            }
        }
        else if (action.type === "exclude_approver_roles" && Array.isArray(action.value)) {
            const roles = action.value
                .filter((r) => typeof r === "string" && r)
                .map((r) => data.t.read("role_" + r.toLowerCase()));
            if (roles.length > 0) {
                label += `: ${roles.join(", ")}`;
            }
        }
        else if (["change_item_detail", "change_invoice_default_field_value"].includes(action.type)){
            const actionValue = isGLSectionWorkflowVariable(action.value)
                ? data.t.read("rule.first_4_digits_of_gl_natural")
                : action.value;
            label += `: ${action.field} ${data.t.read("to").toLocaleLowerCase()} ${actionValue}`;
        }
        else if (action.type === "split_mileage_by_secondary_rate" && action.value) {
            const travelTypeName = action.value.travel_type_id
                ? ((expenseRates?.mileage_travel_types ?? []).find(t => t.id === action.value.travel_type_id)?.name ?? action.value.travel_type_id)
                : null;
            const parts = [
                travelTypeName ? `${data.t.read("mileage_travel_type.label")}: ${travelTypeName}` : "",
                action.value.secondary_rate ? `${data.t.read("rule.secondary_mileage_rate")}: ${action.value.secondary_rate}` : "",
                action.value.billable_cost_type ? `${data.t.read("rule.billable_disbursement_code")}: ${action.value.billable_cost_type}` : "",
            ].filter(Boolean);
            if (parts.length > 0) label += `: ${parts.join(", ")}`;
        }
        else if (action.type === "split_tip_into_separate_item" && action.value) {
            const parts = [
                action.value.cost_type ? `${data.t.read("rule.tip_disbursement_code")}: ${action.value.cost_type}` : "",
            ].filter(Boolean);
            if (parts.length > 0) label += `: ${parts.join(", ")}`;
        }
        else if (action.type === "rename_invoice_file" && action.value) {
            const pieceLabels = {
                supplier_name: data.t.read("rule.filename_piece_supplier_name"),
                invoice_date: data.t.read("rule.filename_piece_invoice_date"),
                invoice_number: data.t.read("rule.filename_piece_invoice_number"),
            };
            const pieces = Array.isArray(action.value.pieces) ? action.value.pieces : [];
            const parts = pieces.map(piece => pieceLabels[piece]).filter(Boolean);
            if (parts.length > 0) {
                label += `: ${parts.join(action.value.separator || "_")}`;
            }
        }
        else if (action.type === "change_item_description" && typeof action.value === "object" && action.value !== null) {
            label += `: ${descriptionTemplateLabel(action.value, key => data.t.read(key))}`;
        }
        else if (action.type === "change_item_description") {
            const parts = [];
            if (action.value) {
                parts.push(action.value);
            }
            if (action.include_supplier_name) {
                parts.push(data.t.read("rule.add_supplier_name"));
            }
            if (action.include_invoice_number) {
                parts.push(data.t.read("rule.add_invoice_number"));
            }
            if (parts.length > 0) {
                label += `: ${parts.join(" + ")}`;
            }
        }
        else if (action.type === "change_all_totals") {
            label += `: ${action.value}%`;
        }
        else if (action.type === "tax_deduction") {
            label += `: ${(action.value)}%`;

        }

        return label;
    };

    // -------------------- RENDERING --------------------
    return (
        <div
            className={`card workflow-step-card w-100 ${!validateStep(step) ? "border-highlight" : ""}`}
            onMouseOver={() => setIsHover(true)}
            onMouseOut={() => setIsHover(false)}
        >
            <button
                type="button"
                className="workflow-step-name"
                onClick={onClickFunc}
            >
                <span className="workflow-step-name__label">{data.t.read("rule.step_name")}</span>
                <span className="workflow-step-name__value">{step.name?.trim() || data.t.read("step")}</span>
            </button>
            <div className="d-flex">
                {/* Main step details area */}
                <div
                    className="card-body p-3 pr-0"
                    onClick={onClickFunc}
                    style={{ cursor: "pointer" }}
                >
                    {/* Render Conditions if any */}
                    {step.condition_logic === "always" && <h6 className="mb-1 text-muted">{data.t.read("rule.always")}</h6>}
                    {step.condition_logic !== "always" && step.conditions.length > 0 && (
                        <div className="mb-2">
                            <h6 className="mb-1 text-muted">
                                {step.condition_logic === "any" ? data.t.read("rule.if_any_match") : data.t.read("rule.if_all_match")}
                            </h6>
                            <ul className="mb-2 ps-3">
                                {step.conditions.map((c) => (
                                    <li
                                        key={c.id}
                                        className={` p-1 border-radius-30 ${!validateCondition(c) ? "border-highlight-0" : ""}`}
                                    >
                                        {getConditionLabel(c)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {/* Render Actions if any */}
                    {step.actions.length > 0 && (
                        <div>
                            <h6 className="mb-1 text-muted">{data.t.read("rule.then")}:</h6>
                            <ul className="mb-0 ps-3">
                                {/* exclude_approver_roles applies step-wide, so show it first
                                    regardless of the order the admin added actions. */}
                                {[...step.actions]
                                    .sort((a, b) =>
                                        (b.type === "exclude_approver_roles") - (a.type === "exclude_approver_roles")
                                    )
                                    .map((action, idx) => (
                                    <li
                                        key={idx}
                                        className={`p-1 border-radius-30 ${!validateAction(action) ? "border-highlight-0" : ""}`}
                                    >
                                        {getActionLabel(action)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {/* The alternate side is read on the canvas like the primary one:
                        its own conditions, then what they trigger. A count alone left
                        a reviewer opening the step to find out what it does. */}
                    {((step.alternate_conditions || []).length > 0
                      || (step.alternate_actions || []).length > 0) && (
                        <div className="mt-2" data-testid="step-card-alternate">
                            {/* One heading like the primary side's, not two lines: the
                                logic keys already open with "if" / "si", so a plain
                                "Otherwise" prefix composes a sentence. */}
                            {/* Flagged when the heading promises a gate the side does not
                                have: with no conditions and a logic other than always it
                                never runs, and the canvas is where that has to show. */}
                            <h6
                                className={`mb-1 text-muted ${
                                    step.alternate_condition_logic !== "always"
                                    && (step.alternate_conditions || []).length === 0
                                        ? "border-highlight-0" : ""}`}
                                data-testid="step-card-alternate-logic"
                            >
                                {step.alternate_condition_logic === "always"
                                    ? data.t.read("rule.otherwise_always")
                                    : `${data.t.read("rule.otherwise")} ${data.t.read(
                                        step.alternate_condition_logic === "any"
                                            ? "rule.if_any_match"
                                            : "rule.if_all_match")}`}
                            </h6>
                            {step.alternate_condition_logic !== "always"
                                && (step.alternate_conditions || []).length > 0 && (
                                <ul className="mb-2 ps-3">
                                    {step.alternate_conditions.map((c) => (
                                        <li
                                            key={c.id}
                                            className={`p-1 border-radius-30 ${!validateCondition(c) ? "border-highlight-0" : ""}`}
                                        >
                                            {getConditionLabel(c)}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {(step.alternate_actions || []).length > 0 && (
                                <>
                                    <h6 className="mb-1 text-muted">{data.t.read("rule.then")}:</h6>
                                    <ul className="mb-0 ps-3">
                                        {/* Same ordering as the primary list: a step-wide
                                            exclusion reads first wherever it sits. */}
                                        {[...step.alternate_actions]
                                            .sort((a, b) =>
                                                (b.type === "exclude_approver_roles") - (a.type === "exclude_approver_roles")
                                            )
                                            .map((action, idx) => (
                                            <li
                                                key={idx}
                                                className={`p-1 border-radius-30 ${!validateAction(action) ? "border-highlight-0" : ""}`}
                                            >
                                                {getActionLabel(action)}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    )}
                </div>
                {/* Remove button, visible only on hover */}
                <div className="d-flex align-items-center p-2">
                    <button
                        className={`btn btn-sm btn-light border-radius-30 position-absolute ${isHover ? "" : "d-none"}`}
                        onClick={removeStep}
                        style={{ top: "-3px", right: "-3px" }}
                        title="Remove step"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default observer(WorkflowStepDisplayCard);
